import { DaoModel } from '@dynameat/models';
import { SectionFilters } from './sectionFilters';

export class MenuFilters extends DaoModel {
  constructor(menuGuid, menuTitle, sections, isManaged) {
    super(null);
    this.menuGuid = menuGuid || '';
    this.menuTitle = menuTitle || '';
    this.sections = sections || [];
    this.isManaged = isManaged;
  }

  get isDisabledInMenuFilters() {
    return !this.isManaged;
  }

  static toInstance(props = {}) {
    const sectionFilters = SectionFilters.toInstanceList(props.sections);
    SectionFilters.populateProductGuidsHierarchically(sectionFilters);
    return new MenuFilters(props.menu_guid, props.menu_title, sectionFilters, props.is_managed);
  }

  #childToParent() {
    return new Map(this.sections.map((section) => [section.sectionGuid, section.parentGuid]));
  }

  #parentToChildren() {
    // A nested map where each level is "parent" : new Map(children).
    // Each children can have its own map, thus the "nested" part.
    // The leaf nodes will have a null value, so they are false evaluated when "getted".
    const childToParent = this.#childToParent();
    const parentToChildrenRecursive = new Map();
    let ancestorChain;
    // For each child previously encountered, we get its ancestor chain.
    // With this chain, we recreate back the nested map structure, creating new maps in the leaf nodes
    // if they get a new child, as get will return a falsy value when the element in the chain is not
    // present or is null.

    // eslint-disable-next-line no-restricted-syntax
    for (const child of childToParent.keys()) {
      ancestorChain = MenuFilters.#getAncestor(childToParent, child);
      let currentMap = parentToChildrenRecursive;
      // eslint-disable-next-line no-restricted-syntax
      for (const ancestor of ancestorChain) {
        if (!currentMap.get(ancestor)) {
          currentMap.set(ancestor, new Map());
        }
        currentMap = currentMap.get(ancestor);
      }
      if (!currentMap.get(child)) {
        currentMap.set(child, null);
      }
    }
    return parentToChildrenRecursive;
  }

  static #getAncestor(childToParent, currentElement) {
    // Iterates up the childToParent dict to get the highest ancestor and returns
    // the list of ancestors from higher to lower.
    const ancestorChain = [];
    let ancestor = currentElement;
    while (ancestor) {
      ancestor = childToParent.get(ancestor);
      if (ancestor) {
        ancestorChain.push(ancestor);
      }
    }
    return ancestorChain.reverse();
  }

  #preorderTreeWalk() {
    // Turns the parent to children nested approach to a flat list, following the preorder path.
    const parentToChildren = this.#parentToChildren();
    const flatList = [];
    let currentItem;
    let currentMap;
    const queue = Array.from(parentToChildren.keys()).map((key) => [key, parentToChildren]);
    while (queue.length > 0) {
      [currentItem, currentMap] = queue.shift();
      flatList.push(currentItem);
      if (currentMap && currentMap.get(currentItem)) {
        const map = currentMap.get(currentItem);
        queue.unshift(...Array.from(map.keys()).map((key) => [key, map]));
      }
    }
    return flatList;
  }

  #sectionNameMap(getMultipleSections = true) {
    // Map with sectionGuid, sectionNameObject.
    const childToParent = this.#childToParent();
    const sections = this.sections.filter((section) => section.allProductGuids.length);
    return new Map(sections.map((section) => [
      section.sectionGuid, section.getRepresentation(childToParent, getMultipleSections)
    ]));
  }

  getOrderedSections(getMultipleSections = true) {
    // Filtering list, with the ordered list of sections and subsections.
    const sectionNameMap = this.#sectionNameMap(getMultipleSections);
    const flatSectionArray = this.#preorderTreeWalk();
    // Filter undefined of get
    return flatSectionArray.map((sectionGuid) => sectionNameMap.get(sectionGuid)).filter((section) => section);
  }

  serialize() {
    return {
      menu_guid: this.menuGuid,
      menu_title: this.menuTitle,
      sections: this.sections.map((section) => section.serialize()),
      is_managed: this.isManaged
    };
  }
}
